import { createPath, createURL } from '@/shared/utils/route';

const { VITE_CLIENT_PAGE_URI } = import.meta.env;

export const ROUTE_PARAMS = {
  CASE_GROUP_ID: 'caseGroupId',
  CASE_ID: 'caseId',
  COMPANY_ID: 'companyId',
  HOMEOWNER_ASSOCIATION_ID: 'homeownerAssociationId',
  INVOICE_ID: 'invoiceId',
  SPREADSHEET_ID: 'spreadsheetId',
} as const;

const ROUTES = {
  ACCESS_KEY_DETAILS: '/access-key/:accessKeyId',
  ACCESS_KEYS: '/access-keys',
  ACCESS_KEYS_LENDINGS: '/access-keys/lendings',
  ACCOUNTING_ENVELOPES: '/accounting/envelopes',
  ACCOUNTING_STATEMENTS: '/accounting/statements/:homeownerAssociationId?',
  ADMINISTRATION: '/administration',
  ADMINISTRATION_BLOG: 'blog',
  ADMINISTRATION_FSMA: 'fsma',
  ADMINISTRATION_HOME_SCORE: 'homescore',
  ADMINISTRATION_INTEGRATORS: 'integrators',
  ADMINISTRATION_KBO: 'kbo',
  ADMINISTRATION_SMS: 'sms',
  ADMINISTRATION_UNUSED_OCTOPUS_DOSSIERS: 'octopus/unused-dossiers',
  BATCH_COMMUNICATION_COMBINED_OVERVIEW: '/batches/created',
  BATCH_COMMUNICATION_DETAIL: '/batch/:batchId',
  BATCH_COMMUNICATION_OVERVIEW: '/batches',
  BUILDING_DETAIL: '/building/:buildingId',
  BUILDING_DETAIL_ASSETS: 'assets',
  BUILDING_DETAIL_DOCUMENTS: 'documents',
  BUILDING_DETAIL_HOMEOWNER_ASSOCIATION_DETAIL: 'homeowner-association/:homeownerAssociationId',
  BUILDING_DETAIL_HOMEOWNER_ASSOCIATION_DETAIL_ACCOUNTING: 'accounting',
  BUILDING_DETAIL_HOMEOWNER_ASSOCIATION_DETAIL_BUDGET: 'budget',
  BUILDING_DETAIL_HOMEOWNER_ASSOCIATION_DETAIL_COMMUNICATION: 'communication',
  BUILDING_DETAIL_HOMEOWNER_ASSOCIATION_DETAIL_COSTS: 'costs',
  BUILDING_DETAIL_HOMEOWNER_ASSOCIATION_DETAIL_DIRECT_DEBIT: 'direct-debit',
  BUILDING_DETAIL_HOMEOWNER_ASSOCIATION_DETAIL_DOCUMENTS: 'documents',
  BUILDING_DETAIL_HOMEOWNER_ASSOCIATION_DETAIL_MEETINGS: 'meetings',
  BUILDING_DETAIL_HOMEOWNER_ASSOCIATION_DETAIL_METERREADINGS: 'meter-readings',
  BUILDING_DETAIL_HOMEOWNER_ASSOCIATION_DETAIL_PROVISIONS: 'provisions',
  BUILDING_DETAIL_HOMEOWNER_ASSOCIATION_DETAIL_SETTINGS: 'settings',
  BUILDING_DETAIL_HOMEOWNER_ASSOCIATION_DETAIL_SYNDICMANAGEMENT: 'syndicManagement',
  BUILDING_DETAIL_RELATIONS: 'relations',
  BUILDING_DETAIL_SUPPLIERS: 'suppliers',
  BUILDING_DETAIL_UNITS: 'units',
  BUILDING_UNIT_DETAIL: '/building/:buildingId/unit/:unitId',
  BUILDINGS_OVERVIEW: '/buildings',
  CALENDAR: '/calendar',
  CALLBACK: '/callback',
  CASE_DETAIL: `/case/:${ROUTE_PARAMS.CASE_ID}`,
  CASE_DETAIL_NAVIGATE: '/case',
  CASE_FINANCE: 'finance/:caseGroupId',
  CASE_MAILBOX: 'mailbox',
  CASE_OVERVIEW: '/cases',
  CASE_OVERVIEW_SEARCH: '/cases?search=',
  COMPANIES_OVERVIEW: '/companies',
  COMPANY_DETAIL: '/company/:companyId',
  CONTACT_DASHBOARD: '/contacts',
  CONTACT_DETAIL: '/contact/:contactId',
  CONTACT_EDIT: '/contact/:contactId/edit',
  CONTRACT_DETAIL: '/contract/:contractId',
  DETAILS_MAILBOX: 'mailbox',
  FINANCE: `/finance/:${ROUTE_PARAMS.CASE_GROUP_ID}`,
  HOA_DEBTOR_MANAGEMENT: '/debtors/hoa',
  HOA_INVOICE_MANAGEMENT: '/hoa/invoices',
  HOA_INVOICE_UPLOAD: '/hoa/invoice',
  HOA_INVOICE_VALIDATE: '/hoa/invoice/:invoiceId/validate',
  HOA_INVOICES_TO_VALIDATE: '/hoa/invoices#validate',
  HOME: '/',
  HOMEOWNER_ASSOCIATION_DETAIL: '/homeowner-association/:homeownerAssociationId',
  HOMEOWNER_ASSOCIATION_DETAIL_ACCOUNTING: 'accounting',
  HOMEOWNER_ASSOCIATION_DETAIL_BUDGET: 'budget',
  HOMEOWNER_ASSOCIATION_DETAIL_BUILDINGS: 'buildings',
  HOMEOWNER_ASSOCIATION_DETAIL_COMMUNICATION: 'communication',
  HOMEOWNER_ASSOCIATION_DETAIL_COSTS: 'costs',
  HOMEOWNER_ASSOCIATION_DETAIL_DEBTORS: 'debtors',
  HOMEOWNER_ASSOCIATION_DETAIL_DIRECT_DEBIT: 'direct-debit',
  HOMEOWNER_ASSOCIATION_DETAIL_DOCUMENTS: 'documents',
  HOMEOWNER_ASSOCIATION_DETAIL_ENVELOPES: 'envelopes',
  HOMEOWNER_ASSOCIATION_DETAIL_INVOICES: 'invoices',
  HOMEOWNER_ASSOCIATION_DETAIL_MEETINGS: 'meetings',
  HOMEOWNER_ASSOCIATION_DETAIL_METERREADINGS: 'meter-readings',
  HOMEOWNER_ASSOCIATION_DETAIL_PROVISIONS: 'provisions',
  HOMEOWNER_ASSOCIATION_DETAIL_RECONCILIATIONS: 'reconciliations',
  HOMEOWNER_ASSOCIATION_DETAIL_SETTINGS: 'settings',
  HOMEOWNER_ASSOCIATION_DETAIL_SYNDICMANAGEMENT: 'syndicManagement',
  HOMEOWNER_ASSOCIATION_MEETING_DETAIL:
    '/homeowner-association/:homeownerAssociationId/meeting/:meetingId',
  INSIGHTS: '/insights',
  MAILBOX: '/mailbox',
  MEETING_DETAIL: '/meeting/:meetingId',
  MEETING_DETAIL_NAVIGATE: '/meeting',
  MEETING_IN_PROGRESS:
    '/homeowner-association/:homeownerAssociationId/meeting/:meetingId/meeting-in-progress',
  MEETING_REGISTRATION:
    '/homeowner-association/:homeownerAssociationId/meeting/:meetingId/meeting-registration',
  MEETINGS_OVERVIEW: '/meetings',
  MEETINGS_PLANNING: '/meetings/planning',
  NOTFOUND: '/notfound',
  POLICY_DETAIL: '/policy/:policyId',
  POLICY_OVERVIEW: '/policies',
  REPAIR_DEBTOR_MANAGEMENT: '/debtors/repair',
  REPAIR_INVOICE_MANAGEMENT: '/repair/invoices',
  REPAIR_INVOICES_ACCEPTED: '/repair/invoices/accepted',
  REPAIR_INVOICES_REJECTED: '/repair/invoices/rejected',
  REPAIR_INVOICES_TO_VALIDATE: '/repair/invoices/toValidate',
  SPREADSHEET_DETAIL: `/spreadsheet/:${ROUTE_PARAMS.SPREADSHEET_ID}`,
  SUPPORT_OVERVIEW: '/support',
  SYNDIC_MANAGMENT_OVERVIEW: '/syndic-management',
  TASKS_OVERVIEW: '/tasks',
  TEAM_SETTINGS: '/team/settings',
  TEAM_SETTINGS_AI_EMAIL_DRAFTING: 'ai-email-drafting',
  TEAM_SETTINGS_ASSETS: 'assets',
  TEAM_SETTINGS_AUTOMATIONS: 'automations',
  TEAM_SETTINGS_BUILDING_PORTAL: 'building-portal',
  TEAM_SETTINGS_CASE: 'case',
  TEAM_SETTINGS_COMMUNICATION: 'communication',
  TEAM_SETTINGS_EMAILACCOUNT_DETAIL: 'email-accounts',
  TEAM_SETTINGS_FINANCE: 'finance',
  TEAM_SETTINGS_GENERAL: 'general',
  TEAM_SETTINGS_INTEGRATIONS: 'integrations',
  TEAM_SETTINGS_LABELS: 'labels',
  TEAM_SETTINGS_MEETINGS: 'meetings',
  TEAM_SETTINGS_MEETINGS_MEETINGTYPE_DETAIL: 'meeting-type/:meetingTypeId',
  TEAM_SETTINGS_PERFORMANCES: 'performances',
  TEAM_SETTINGS_REPAIR: 'repair',
  TEAM_SETTINGS_TASKTYPES: 'task-types',
  TEAM_SETTINGS_TEAMMEMBERS: 'team-members',
  USER_SETTINGS: '/user/settings',
  USER_SETTINGS_CALENDARS: 'calendars',
  USER_SETTINGS_EMAILACCOUNTS: 'email-accounts',
  USER_SETTINGS_GENERAL: 'general',
} as const;

export const SEARCH_PARAMS = {
  CASE_ID: 'caseId',
  FROM: 'from',
} as const;

export const externalURLFactories = {
  clientPage: ({ caseId, locale }: { caseId: string; locale: string }) =>
    createURL(`${VITE_CLIENT_PAGE_URI}/${locale.slice(0, 2)}`, {
      searchParams: {
        [SEARCH_PARAMS.CASE_ID]: caseId,
      },
    }),
};

export const routeFactories = {
  caseDetail: ({ caseId }: { caseId: string }) =>
    createPath(ROUTES.CASE_DETAIL, {
      routeParams: {
        [ROUTE_PARAMS.CASE_ID]: caseId,
      },
    }),
  caseFinance: ({ caseGroupId }: { caseGroupId: string }) =>
    createPath(ROUTES.FINANCE, {
      routeParams: {
        [ROUTE_PARAMS.CASE_GROUP_ID]: caseGroupId,
      },
    }),
  companyDetail: ({ companyId }: { companyId: string }) =>
    createPath(ROUTES.COMPANY_DETAIL, {
      routeParams: {
        [ROUTE_PARAMS.COMPANY_ID]: companyId,
      },
    }),
  hoaInvoiceValidate: ({
    invoiceId,
    homeownerAssociationId,
  }: {
    invoiceId: string;
    homeownerAssociationId?: string | null;
  }) =>
    createPath(ROUTES.HOA_INVOICE_VALIDATE, {
      routeParams: { [ROUTE_PARAMS.INVOICE_ID]: invoiceId },
      searchParams: homeownerAssociationId
        ? { [SEARCH_PARAMS.FROM]: homeownerAssociationId }
        : undefined,
    }),
  homeownerAssociationAccounting: ({
    homeownerAssociationId,
  }: {
    homeownerAssociationId: string;
  }) =>
    createPath(
      `${ROUTES.HOMEOWNER_ASSOCIATION_DETAIL}/${ROUTES.HOMEOWNER_ASSOCIATION_DETAIL_ACCOUNTING}`,
      {
        routeParams: { [ROUTE_PARAMS.HOMEOWNER_ASSOCIATION_ID]: homeownerAssociationId },
      },
    ),
  homeownerAssociationDetail: ({ homeownerAssociationId }: { homeownerAssociationId: string }) =>
    createPath(ROUTES.HOMEOWNER_ASSOCIATION_DETAIL, {
      routeParams: { [ROUTE_PARAMS.HOMEOWNER_ASSOCIATION_ID]: homeownerAssociationId },
    }),
  spreadsheetDetail: ({
    caseId,
    spreadsheetId,
  }: {
    caseId?: string | null;
    spreadsheetId: string;
  }) =>
    createPath(ROUTES.SPREADSHEET_DETAIL, {
      routeParams: { [ROUTE_PARAMS.SPREADSHEET_ID]: spreadsheetId },
      searchParams: caseId ? { [SEARCH_PARAMS.FROM]: caseId } : undefined,
    }),
};

export default ROUTES;
